/**
 * PF Revamp — legacy event parity map.
 *
 * Funnel continuity (§11) depends on the legacy Firebase / Mixpanel /
 * Intercom events keeping their exact names and firing at the equivalent
 * revamp code-site. This file is the single source of truth for that
 * mapping. The dispatcher (`usePfRevampAnalytics`) fires BOTH the revamp
 * namespaced event AND any mapped legacy events in one go.
 *
 * Where revamp consolidates multiple legacy events (e.g., the legacy flow
 * had separate Category + ProductSelection screens, revamp fuses them into
 * one Catalogue screen), the revamp site fires ALL the legacy names it
 * subsumes.
 */

import { AnalyticsEvent } from 'utils/analytics'
import type { ConfigurationFlowId } from 'models/purchaseFlowRevamp'

import type { ConfigPanelId, FloorPlanChoice, PfRevampEvent, PfStepName, ScreenEnteredPayload } from './events'
import { PfRevampEventName } from './events'

/**
 * Maps a step name to its legacy on-enter screen event(s).
 *
 * Fired by the step that is BEING ENTERED (`SCREEN_ENTERED`), not by the
 * navigation that got there. Legacy hung these off the navigation, which meant
 * the step the flow OPENS on was never counted — so legacy can tell you how many
 * people reached instructions but not how many started, and the conversion rate
 * a canary needs was never computable. v2 fires on arrival, so the entry screen
 * counts like every other.
 */
export const LEGACY_ON_ENTER_EVENTS: Record<PfStepName, readonly string[]> = {
  // `enters_order_category_selection_screen` is deliberately absent. v2 has no
  // category step — the catalogue shows every category at once — so there is no
  // moment a client "enters category selection", and a v2 count would measure a
  // different act than legacy's under the same name.
  catalogue: [AnalyticsEvent.PURCHASE_FLOW_ENTERS_ORDER_PRODUCTS_STEP],
  // Configuration screen is revamp-only — no direct legacy analog (flow-specific
  // screens in legacy were per-product, not a shared configuration step).
  configuration: [],
  instructions: [AnalyticsEvent.PURCHASE_FLOW_ENTERS_ORDER_INSTRUCTIONS_STEP],
  billing: [AnalyticsEvent.PURCHASE_FLOW_ENTERS_ORDER_BILLING_STEP],
  summary: [AnalyticsEvent.PURCHASE_FLOW_ENTERS_ORDER_VALIDATION_STEP],
}

/** Legacy next / previous step events. */
export const LEGACY_STEP_NAV = {
  NEXT: 'Clicks_next_step_purchase_flow',
  PREVIOUS: 'Clicks_previous_step_purchase_flow',
} as const

/**
 * Legacy's per-configuration-screen events, keyed by the v2 flow that now shows
 * the same form.
 *
 * Partial on purpose. A flow absent here had no legacy screen event — the two
 * energy certificates were legacy steps that announced themselves only through
 * their inner `opens_*` panels, and `editing_config` / `ai_editing` are v2's own.
 * Minting a name for those would create a series with nothing to compare against.
 */
export const LEGACY_CONFIG_SCREEN_BY_FLOW: Partial<Record<ConfigurationFlowId, string>> = {
  property_info: AnalyticsEvent.ENTER_ORDER_PROPERTY_INFO_PAGE_SCREEN,
  power_of_attorney: AnalyticsEvent.ENTER_ORDER_POA_PAGE_SCREEN,
  measurement: AnalyticsEvent.ENTER_ORDER_MEASUREMENTS_PAGE_SCREEN,
  floor_plan_certification: AnalyticsEvent.ENTER_ORDER_LIVING_AREA_CALCULATION_PAGE_SCREEN,
  floor_plan_config: AnalyticsEvent.ENTER_ORDER_FLOOR_PLAN_PAGE_SCREEN,
  staging: AnalyticsEvent.ENTER_ORDER_STAGING_PAGE_SCREEN,
}

/**
 * Flows whose module shows several screens under one tab and therefore announces
 * them itself. The configuration shell must NOT announce these on tab
 * activation: the document configurator opens on its property-information page,
 * so counting a power-of-attorney screen at that moment would record a form the
 * client has not seen yet.
 */
export const MODULE_ANNOUNCED_CONFIG_FLOWS: ReadonlySet<ConfigurationFlowId> =
  new Set<ConfigurationFlowId>(['property_info', 'power_of_attorney'])

/**
 * Legacy's within-a-form `opens_*` events, keyed by the panel that now shows the
 * same fields. Legacy fired these on the panel component's own mount, which in
 * both flows is the moment the client selects it — so v2 fires on selection, and
 * the two count the same act even though one certificate keeps its panels
 * mounted and hidden.
 */
export const LEGACY_PANEL_EVENTS: Record<ConfigPanelId, string> = {
  PROPERTY_INFORMATION: AnalyticsEvent.OPENS_PROPERTY_INFORMATION,
  BUILDING_LAYOUT: AnalyticsEvent.OPENS_BUILDING_LAYOUT,
  CONSTRUCTION_METHOD: AnalyticsEvent.OPENS_CONSTRUCTION_METHOD,
  // Legacy's component was VentilationAndHeating; v2 renamed the panel, not the
  // measurement. The legacy name wins, so the series survives the rename.
  ENERGY_AND_HEATING: AnalyticsEvent.OPENS_VENTILATION_HEATING,
  ADDITIONAL_INFORMATION: AnalyticsEvent.OPENS_ADDITIONAL_INFORMATION,
  HEATING_SYSTEM: AnalyticsEvent.OPENS_HEATING_SYSTEM,
  CONSUMPTION_INFORMATION: AnalyticsEvent.OPENS_CONSUMPTION_INFORMATION,
}

/** Legacy's per-choice floor-plan events, keyed by the v2 choice. */
export const LEGACY_FLOOR_PLAN_EVENTS: Record<FloorPlanChoice, string> = {
  create_from_scratch: AnalyticsEvent.FLOOR_PLAN_CREATE_SELECTED,
  template: AnalyticsEvent.FLOOR_PLAN_TEMPLATE_SELECTED,
  render_type: AnalyticsEvent.FLOOR_PLAN_TYPE_SELECTED,
  color: AnalyticsEvent.FLOOR_PLAN_COLOR_SELECTED,
  logo: AnalyticsEvent.FLOOR_PLAN_LOGO_UPLOADED,
}

/**
 * Legacy's "the client finished this form" events, keyed by the v2 flow whose
 * save is now that moment.
 *
 * Legacy fired them from the footer's Next handler for three specific steps; v2
 * saves a configuration explicitly, which is the same act. The form CONTENTS
 * legacy attached are deliberately not reproduced — they were a per-step copy of
 * three whole DTOs, and a funnel needs the moment, not the answers. If the
 * answers are wanted they should be read from the saved configuration, not
 * re-serialized into an event body.
 */
export const LEGACY_CONFIG_SAVED_EVENTS: Partial<Record<ConfigurationFlowId, string>> = {
  floor_plan_config: AnalyticsEvent.FLOOR_PLAN_OPTIONS_SELECTED,
  floor_plan_certification: AnalyticsEvent.LIVING_AREA_CALCULATION_REQUIREMENT_SET_UP,
  measurement: AnalyticsEvent.MEASUREMENTS_REQUIREMENT_SET_UP,
}

/** Legacy's two land-register events, which v2 treats as one decision. */
export const LEGACY_LAND_REGISTER_EVENTS = {
  MAP: AnalyticsEvent.PARCEL_SELECTED,
  MANUAL: AnalyticsEvent.EDIT_LAND_REGISTER_SHEET_MANUALLY,
} as const

export const LEGACY_CT_UNAVAILABLE = AnalyticsEvent.FAVOURITE_CREATIVES_UNAVAILABLE

/** Legacy's AI-editing event. Its `flow` field told gallery and purchase flow
 *  apart, so v2 must keep sending the purchase-flow value or the two mix. */
export const LEGACY_AI_OPTIONS = 'AI_options_selected'
export const LEGACY_AI_OPTIONS_FLOW = 'purchase-flow'

export const LEGACY_ADDRESS_MODE_EVENTS = {
  MANUAL: AnalyticsEvent.MANUAL_ADDRESS_SELECTED,
  SEARCH: AnalyticsEvent.MANUAL_ADDRESS_UNSELECTED,
} as const

export const LEGACY_CLOSE_FLOW = 'Close_purchase_flow'

/**
 * Legacy's add-on pair. One name for every list the drawer renders — the
 * relation and the role ride the v2 payload rather than splitting the series,
 * so the legacy history stays a single comparable line.
 */
export const LEGACY_ADDON_EVENTS = {
  SELECTED: AnalyticsEvent.PURCHASE_FLOW_OPTION_SELECTED,
  REMOVED: AnalyticsEvent.PURCHASE_FLOW_OPTION_REMOVED,
} as const

/** Legacy's flow-load anchor. Legacy carries `surface`, `load_status` and
 *  `first_screen`; v2 sends the same three so one series covers both. */
export const LEGACY_ORDER_FLOW_LOADED = AnalyticsEvent.ORDER_FLOW_LOADED

/** The only `surface` v2 has. Legacy's other value, `status_view`, is the embed
 *  status card, which v2 does not render. */
export const LEGACY_ORDER_FLOW_SURFACE = 'order_flow'

/** Legacy order-placed event fires on Intercom + Userpilot directly. */
export const LEGACY_ORDER_PURCHASED = 'Purchased'

/** Legacy's `Flow` tag on CC-email events — the literal value of
 *  `constants/misc` `Flow.PURCHASE_FLOW`, which is a HUMAN string, not the
 *  enum key. Pinned here so a rename cannot split the series silently. */
export const LEGACY_PURCHASE_FLOW_TAG = 'Purchase flow'

/**
 * Instructions-step legacy names. Values copied from
 * `utils/analytics/AnalyticsEvent.ts` rather than imported, matching how the
 * rest of this bridge pins legacy names as literals — the point is that a
 * rename on either side cannot silently split a funnel series.
 */
export const LEGACY_INSTRUCTION_EVENTS = {
  ORGANISATION_METHOD_SELECTED: AnalyticsEvent.PURCHASE_FLOW_ORGANISATION_METHOD_SELECTED,
  I_ON_SITE_SELECTED: 'I_am_on_site_selected',
  FAST_DELIVERY_SELECTED: AnalyticsEvent.PURCHASE_FLOW_FAST_DELIVERY_SELECTED,
  FAST_DELIVERY_REMOVED: AnalyticsEvent.PURCHASE_FLOW_FAST_DELIVERY_REMOVED,
  SELECT_CREATIVE_OPTION_OPENED: AnalyticsEvent.SELECT_CREATIVE_OPTION_OPENED,
  CREATIVE_SELECTED_BY_CLIENT: AnalyticsEvent.CREATIVE_SELECTED_BY_CLIENT,
  /** Billing step. Capital P in legacy's value — copied, not corrected. */
  PAYMENT_METHOD_SELECTED: 'Payment_method_selected',
  CC_EMAIL_ADDED: AnalyticsEvent.CC_EMAIL_ADDED,
  CC_EMAIL_REMOVED: AnalyticsEvent.CC_EMAIL_REMOVED,
} as const

/** The CRM embed's events. Legacy also sent Size_property_selected from the web app's band slider, which v2 has not. */
export const LEGACY_EMBED_EVENTS = {
  SIZE_PROPERTY_SELECTED: AnalyticsEvent.PURCHASE_FLOW_SIZE_PROPERTY_SELECTED,
  SIZE_GATE_BLOCKED: AnalyticsEvent.PURCHASE_FLOW_EMBED_SIZE_GATE_BLOCKED,
  REPRICED: AnalyticsEvent.PURCHASE_FLOW_EMBED_REPRICED,
  OVERLAY_SHOWN: AnalyticsEvent.PURCHASE_FLOW_EMBED_OVERLAY_SHOWN,
  CONSENT_RECORDED: AnalyticsEvent.PURCHASE_FLOW_EMBED_CONSENT_RECORDED,
} as const

/** Legacy's billing and validation entries said what the client could see; its other entries carried nothing. */
const screenEntryProps = ({ step, ctaInViewport, stepContentOverflows, invoiceOption }: ScreenEnteredPayload): Record<string, unknown> => {
  if (step !== 'billing' && step !== 'summary') return {}
  const layout = { cta_in_viewport: ctaInViewport ?? null, step_content_overflows: stepContentOverflows ?? null }
  return step === 'billing' ? { invoice_option: invoiceOption ?? null, ...layout } : layout
}

/**
 * Returns the legacy event names to fire alongside a given revamp event.
 * Empty array for events with no legacy analog.
 */
export const legacyEventsFor = (event: PfRevampEvent): readonly string[] => {
  switch (event.type) {
    case PfRevampEventName.STEP_ADVANCED:
    case PfRevampEventName.STEP_ADVANCE_BLOCKED:
      return [LEGACY_STEP_NAV.NEXT]
    case PfRevampEventName.STEP_RETURNED:
      return [LEGACY_STEP_NAV.PREVIOUS]
    case PfRevampEventName.SCREEN_ENTERED:
      return LEGACY_ON_ENTER_EVENTS[event.payload.step]
    case PfRevampEventName.FLOW_LOADED:
      return [LEGACY_ORDER_FLOW_LOADED]
    case PfRevampEventName.ADDON_TOGGLED:
      return [event.payload.selected
        ? LEGACY_ADDON_EVENTS.SELECTED
        : LEGACY_ADDON_EVENTS.REMOVED]
    case PfRevampEventName.CONFIG_PANEL_OPENED:
      return [LEGACY_PANEL_EVENTS[event.payload.panel]]
    case PfRevampEventName.FLOOR_PLAN_CHOICE_MADE:
      return [LEGACY_FLOOR_PLAN_EVENTS[event.payload.choice]]
    case PfRevampEventName.CT_BOOKING_UNAVAILABLE:
      return [LEGACY_CT_UNAVAILABLE]
    case PfRevampEventName.AI_EDITING_OPTION_SET:
      return [LEGACY_AI_OPTIONS]
    case PfRevampEventName.ADDRESS_ENTRY_MODE_CHANGED:
      return [event.payload.manual
        ? LEGACY_ADDRESS_MODE_EVENTS.MANUAL
        : LEGACY_ADDRESS_MODE_EVENTS.SEARCH]
    case PfRevampEventName.LAND_REGISTER_SOURCE_CHOSEN:
      return [event.payload.source === 'map'
        ? LEGACY_LAND_REGISTER_EVENTS.MAP
        : LEGACY_LAND_REGISTER_EVENTS.MANUAL]
    case PfRevampEventName.PRODUCT_CONFIG_SAVED: {
      const savedName = LEGACY_CONFIG_SAVED_EVENTS[event.payload.flowId]
      return savedName ? [savedName] : []
    }
    case PfRevampEventName.CONFIG_SCREEN_ENTERED: {
      const legacyName = LEGACY_CONFIG_SCREEN_BY_FLOW[event.payload.flowId]
      return legacyName ? [legacyName] : []
    }
    case PfRevampEventName.FLOW_CLOSED:
      return [LEGACY_CLOSE_FLOW]
    case PfRevampEventName.SOURCE_IMAGE_QUALITY_CHECKED:
      // Legacy fired one per refused image.
      return Array.from({ length: event.payload.refusedCount }, () => AnalyticsEvent.VISUAL_QUALITY_DECLINED)
    case PfRevampEventName.ORDER_PLACED:
      // Legacy Purchased fires on Intercom + Userpilot; the standard
      // logAnalyticsEvent dispatcher handles Intercom. Userpilot gets a
      // dedicated call in the dispatcher (see usePfRevampAnalytics).
      return [LEGACY_ORDER_PURCHASED]
    case PfRevampEventName.MEETING_MODE_SELECTED:
      return [LEGACY_INSTRUCTION_EVENTS.ORGANISATION_METHOD_SELECTED]
    case PfRevampEventName.MEETING_CONTACT_PREFILLED:
      // Legacy fired this on tick AND untick. Reproduced deliberately: firing
      // only on tick would make the v2 series read as a drop against legacy's
      // history rather than as the same behaviour measured correctly.
      return [LEGACY_INSTRUCTION_EVENTS.I_ON_SITE_SELECTED]
    case PfRevampEventName.EXTRA_TOGGLED:
      // Legacy's generic extras toggle fired the FAST_DELIVERY-named pair for
      // every extra kind, so the legacy series is "an extra was toggled", not
      // "fast delivery was toggled". Kept as-is so the series stays continuous;
      // `kind` on the revamp event is where the real breakdown lives.
      return [event.payload.selected
        ? LEGACY_INSTRUCTION_EVENTS.FAST_DELIVERY_SELECTED
        : LEGACY_INSTRUCTION_EVENTS.FAST_DELIVERY_REMOVED]
    case PfRevampEventName.CT_BOOKING_OPENED:
      return [LEGACY_INSTRUCTION_EVENTS.SELECT_CREATIVE_OPTION_OPENED]
    case PfRevampEventName.CT_BOOKING_CHOSEN:
      return [LEGACY_INSTRUCTION_EVENTS.CREATIVE_SELECTED_BY_CLIENT]
    case PfRevampEventName.PAYMENT_METHOD_SELECTED:
      return [LEGACY_INSTRUCTION_EVENTS.PAYMENT_METHOD_SELECTED]
    case PfRevampEventName.CC_EMAIL_CHANGED:
      return [event.payload.added
        ? LEGACY_INSTRUCTION_EVENTS.CC_EMAIL_ADDED
        : LEGACY_INSTRUCTION_EVENTS.CC_EMAIL_REMOVED]
    case PfRevampEventName.PROPERTY_SIZE_SELECTED:
      return [LEGACY_EMBED_EVENTS.SIZE_PROPERTY_SELECTED]
    case PfRevampEventName.SIZE_GATE_BLOCKED:
      return [LEGACY_EMBED_EVENTS.SIZE_GATE_BLOCKED]
    case PfRevampEventName.CART_REPRICED:
      return [LEGACY_EMBED_EVENTS.REPRICED]
    case PfRevampEventName.EMBED_OVERLAY_SHOWN:
      return [LEGACY_EMBED_EVENTS.OVERLAY_SHOWN]
    case PfRevampEventName.EMBED_CONSENT_RECORDED:
      return [LEGACY_EMBED_EVENTS.CONSENT_RECORDED]
    default:
      return []
  }
}

/** Shape of the legacy event payload. Kept loose — legacy call sites used
 *  ad-hoc shapes per event, and the funnel key is the NAME, not the body. */
export const legacyPayloadFor = (event: PfRevampEvent, legacyName: string): Record<string, unknown> => {
  switch (event.type) {
    case PfRevampEventName.STEP_ADVANCED: {
      const { fromStep, toStep, ccEmailsAddedCount, onSiteContactIsOrderer } = event.payload
      return {
        CurrentStep: fromStep,
        NewStep: toStep,
        validation_passed: true,
        validation_errors: [],
        ...(ccEmailsAddedCount !== undefined && { cc_emails_added_count: ccEmailsAddedCount }),
        ...(onSiteContactIsOrderer !== undefined && { on_site_contact_is_orderer: onSiteContactIsOrderer }),
      }
    }
    case PfRevampEventName.STEP_ADVANCE_BLOCKED:
      return {
        CurrentStep: event.payload.fromStep,
        NewStep: event.payload.toStep,
        validation_passed: false,
        validation_errors: event.payload.validationErrors,
      }
    case PfRevampEventName.STEP_RETURNED:
      return { CurrentStep: event.payload.fromStep, NewStep: event.payload.toStep }
    case PfRevampEventName.SCREEN_ENTERED:
      return screenEntryProps(event.payload)
    case PfRevampEventName.CONFIG_SCREEN_ENTERED:
    case PfRevampEventName.CONFIG_PANEL_OPENED:
      // On-enter screen events took no payload in legacy.
      return {}
    case PfRevampEventName.FLOOR_PLAN_CHOICE_MADE:
      // Legacy named the value field after the choice; the names are kept so a
      // chart keyed on them keeps working.
      switch (event.payload.choice) {
        case 'color':
          return { color: event.payload.value }
        case 'render_type':
          return { type: event.payload.value }
        case 'template':
          return { templateId: event.payload.value, type: event.payload.renderType }
        default:
          return {}
      }
    case PfRevampEventName.PRODUCT_CONFIG_SAVED:
      // See LEGACY_CONFIG_SAVED_EVENTS: the moment is reproduced, the form
      // contents deliberately are not.
      return {}
    case PfRevampEventName.CT_BOOKING_UNAVAILABLE:
      return { reason: event.payload.reason, trigger: event.payload.trigger }
    case PfRevampEventName.AI_EDITING_OPTION_SET:
      return {
        skyReplacement: event.payload.skyReplacement,
        skyType: event.payload.skyType,
        flow: LEGACY_AI_OPTIONS_FLOW,
      }
    case PfRevampEventName.ADDRESS_ENTRY_MODE_CHANGED:
      return {}
    case PfRevampEventName.LAND_REGISTER_SOURCE_CHOSEN:
      // Legacy attached the whole parcel selection plus a cart snapshot. Neither
      // is a funnel input, and the parcel list is property data that has no place
      // in an analytics body.
      return {}
    case PfRevampEventName.ADDON_TOGGLED:
      // Legacy sent the PARENT's kind and type, capitalised. v2's catalogue
      // publishes no product type at all (the v2 wire carries `kind` only), so
      // that key is dropped rather than sent empty — an always-undefined
      // dimension in a chart is worse than an absent one. The kind is likewise
      // omitted when the catalogue did not publish it.
      return event.payload.parentKind ? { ProductKind: event.payload.parentKind } : {}
    case PfRevampEventName.FLOW_LOADED:
      // `load_status` is success unless the embed's property fetch failed and the
      // error screen is what rendered; that case mirrors legacy's failure body.
      // Legacy's `load_time_ms` and `supportsContainerQueries` are embed-only
      // and container-query diagnostics respectively — neither applies to v2,
      // and sending a fabricated value would be worse than sending none.
      if (event.payload.failureReason) {
        return {
          surface: LEGACY_ORDER_FLOW_SURFACE,
          load_status: 'failure',
          failure_reason: event.payload.failureReason,
          first_screen: 'error',
        }
      }
      return {
        surface: LEGACY_ORDER_FLOW_SURFACE,
        load_status: 'success',
        first_screen: event.payload.firstScreen,
      }
    case PfRevampEventName.FLOW_CLOSED:
      return { OrderStep: event.payload.fromStep }
    case PfRevampEventName.ORDER_PLACED:
      return { orderId: event.payload.orderId }
    // The instructions-step legacy payloads are reproduced field-for-field, so a
    // dashboard built on the legacy names keeps working across the cutover.
    case PfRevampEventName.MEETING_MODE_SELECTED:
      return { organizationMethod: event.payload.organizationMethod }
    case PfRevampEventName.MEETING_CONTACT_PREFILLED:
      return { organizationMethod: event.payload.organizationMethod }
    case PfRevampEventName.EXTRA_TOGGLED:
      // Legacy sent `{}` for both directions — the name carried the direction.
      return {}
    case PfRevampEventName.CT_BOOKING_OPENED:
      return {
        lowestTransportCost: event.payload.lowestTravelCost,
        highestTransportCost: event.payload.highestTravelCost,
        currency: event.payload.currency,
      }
    case PfRevampEventName.CT_BOOKING_CHOSEN:
      return {
        transportCosts: event.payload.travelCost,
        creativeFullName: event.payload.creativeFullName,
      }
    case PfRevampEventName.PAYMENT_METHOD_SELECTED:
      // Legacy's field names, capitalisation included.
      return {
        PaymentMethodSelected: event.payload.paymentMethod,
        IsSubscribed: event.payload.isSubscribed,
      }
    case PfRevampEventName.CC_EMAIL_CHANGED:
      // Legacy tagged both with the flow they came from.
      return { Flow: LEGACY_PURCHASE_FLOW_TAG }
    case PfRevampEventName.PROPERTY_SIZE_SELECTED:
      return { Segment: event.payload.segment, size_prefilled: event.payload.sizePrefilled, gate_shown: event.payload.gateShown, size_source: event.payload.sizeSource }
    case PfRevampEventName.SIZE_GATE_BLOCKED:
      return { reason: event.payload.reason }
    case PfRevampEventName.CART_REPRICED: {
      const { coveredCount, chargedTotal, discountedCount, swappedCount } = event.payload
      return { coveredCount, chargedTotal, partner_discount_shown: discountedCount > 0, discountedCount, swappedCount }
    }
    case PfRevampEventName.EMBED_OVERLAY_SHOWN:
      // Constants: the overlay only shows while consent is missing, and legacy sends them so the spec's fields exist.
      return { overlay_shown: true, consent_shown: true }
    case PfRevampEventName.EMBED_CONSENT_RECORDED:
      return { consentTypes: event.payload.consentTypes }
    default:
      return {}
  }
}
